const PHYSICAL_KEYS: Record<string, string> = { Period: '.', Comma: ',', Slash: '/' };

/**
 * Lower-cased key for shortcut matching. Latin layouts (incl. AZERTY, Dvorak) match by the typed key;
 * non-Latin layouts (Cyrillic, Greek, …) fall back to the Latin key at the same physical position.
 */
export function shortcutKey(event: Pick<KeyboardEvent, 'key' | 'code'>): string {
  // Chrome's autofill fires a keydown with no `key`.
  if (typeof event.key !== 'string') return '';
  const key = event.key.toLowerCase();
  if (key.length !== 1 || key.charCodeAt(0) < 128) return key;
  const letter = /^Key([A-Z])$/.exec(event.code)?.[1];
  return letter ? letter.toLowerCase() : (PHYSICAL_KEYS[event.code] ?? key);
}

/** ⌘ or Ctrl. Fixed for the page's life; the `isMac` parameters below exist for tests. */
export const IS_MAC = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

export interface ShortcutKeys {
  /** Lower-cased Latin key, as returned by `shortcutKey`. */
  key: string;
  /** Needs ⌘ (macOS) / Ctrl (elsewhere). */
  mod?: boolean;
}

/** Whether `event` is `shortcut`: its key per `shortcutKey`, ⌘/Ctrl per platform (never the other one), no Alt. */
export function isShortcut(
  event: Pick<KeyboardEvent, 'key' | 'code' | 'altKey' | 'shiftKey' | 'ctrlKey' | 'metaKey'>,
  shortcut: ShortcutKeys,
  isMac = IS_MAC
): boolean {
  if (event.altKey) return false;
  const [mod, otherMod] = isMac ? [event.metaKey, event.ctrlKey] : [event.ctrlKey, event.metaKey];
  if (otherMod || mod !== !!shortcut.mod) return false;
  const key = shortcutKey(event);
  // Shift+letter is another shortcut (Shift+S, Firefox's Ctrl+Shift+K console). Shift stays allowed for
  // punctuation, which needs it on some layouts (AZERTY '.').
  if (event.shiftKey && /^[a-z]$/.test(key)) return false;
  return key === shortcut.key;
}

/** Key-cap text: `⌘K` on macOS, `Ctrl K` elsewhere; single keys upper-cased. */
export function formatShortcutKey(shortcut: ShortcutKeys, isMac = IS_MAC): string {
  const key = shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key;
  if (!shortcut.mod) return key;
  return isMac ? `⌘${key}` : `Ctrl ${key}`;
}
